import React, { useState } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";

import { useEditFlowVersion } from "src/api/flowVersionQueries";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Switch } from "src/base-components/Switch";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import {
  JobConfigurationExecutionMode,
  JobRateLimitInterval,
} from "src/jobs/types";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { errorMessage } from "src/utils/stringUtils";

const Label: React.FC<{
  label: string;
  description: string;
  fields: React.ReactNode;
}> = ({ label, description, fields }) => {
  return (
    <div className="flex w-full grow flex-col gap-1 pl-3">
      <div className="flex gap-x-1">
        <span className="font-inter-semibold-13px text-gray-800">{label}</span>
        {description && (
          <Tooltip title={description} asChild>
            <Icon icon={faInfoCircle} size="xs" />
          </Tooltip>
        )}
      </div>
      {fields}
    </div>
  );
};

export type LimitType = "concurrency" | "rate-limit";

export type LongTestRunConfigForm = {
  limitType: LimitType;
  executionMode: JobConfigurationExecutionMode;
  executionConcurrency: number;
  rateLimit: number;
  rateLimitInterval: JobRateLimitInterval;
};

const LimitTypeSelector: React.FC = () => {
  const { register, control, setValue, watch } =
    useFormContext<LongTestRunConfigForm>();

  const value = watch("limitType");

  return (
    <SimpleRadioGroup
      className="gap-y-3"
      orientation="vertical"
      value={value}
      onValueChange={(value) => setValue("limitType", value as LimitType)}
    >
      <SimpleRadioGroup.Item
        label={
          <Label
            description="Specify how many decisions are permitted to run in parallel. A higher concurrency level can expedite the completion of job but may place more demand on external connections. Adjust this setting to optimize performance without straining your external connections."
            fields={
              <Input
                type="number"
                {...register("executionConcurrency", {
                  valueAsNumber: true,
                  min: 1,
                  max: 50,
                })}
                disabled={value === "rate-limit"}
                fullWidth
              />
            }
            label="Execution concurrency"
          />
        }
        labelClassName="w-full"
        value="concurrency"
        alignTop
      />
      <SimpleRadioGroup.Item
        label={
          <Label
            description="Specify the maximum number of decisions that can be executed per unit time. This helps prevent overloading your system and ensures fair resource distribution."
            fields={
              <div className="flex items-center gap-x-2">
                <div className="flex-grow">
                  <Input
                    type="number"
                    {...register("rateLimit", { valueAsNumber: true, min: 1 })}
                    disabled={value === "concurrency"}
                    fullWidth
                  />
                </div>
                <span className="whitespace-nowrap font-inter-normal-12px text-gray-600">
                  decisions per
                </span>
                <Controller
                  control={control}
                  name="rateLimitInterval"
                  render={({ field }) => (
                    <div className="w-28">
                      <Select
                        disabled={value === "concurrency"}
                        options={[
                          { key: JobRateLimitInterval.HOUR, value: "Hour" },
                          { key: JobRateLimitInterval.MINUTE, value: "Minute" },
                          { key: JobRateLimitInterval.SECOND, value: "Second" },
                          {
                            key: JobRateLimitInterval.MILLISECOND,
                            value: "Millisecond",
                          },
                        ]}
                        value={field.value}
                        fullWidth
                        onChange={field.onChange}
                      />
                    </div>
                  )}
                />
              </div>
            }
            label="Rate limit"
          />
        }
        labelClassName="w-full"
        value="rate-limit"
        alignTop
      />
    </SimpleRadioGroup>
  );
};

const StatusPill: React.FC<{
  status: string;
}> = ({ status }) => {
  return (
    <Pill size="sm" variant="gray">
      <Pill.Text fontType="code">{status}</Pill.Text>
    </Pill>
  );
};

export const LongTestRunsConfig: React.FC<{ forceEnabled: boolean }> = ({
  forceEnabled,
}) => {
  const { version } = useAuthoringContext();
  // using local state to avoid network delays
  const [isEnabled, setEnabled] = useState(
    Boolean(version.meta.enable_long_test_runs),
  );
  const effectiveIsEnabled = isEnabled || forceEnabled;
  const { mutateAsync } = useEditFlowVersion();

  const updateFlowVersion = useDebounceCallback(async (value: boolean) => {
    try {
      const updatedVersion = await mutateAsync({
        version,
        enable_long_test_runs: value,
      });
      setEnabled(Boolean(updatedVersion.meta.enable_long_test_runs));
    } catch (error) {
      toastActions.failure({
        title: "Failed to enable long test runs",
        description: errorMessage(error),
      });
    }
  }, 200);

  return (
    <div className="flex flex-col gap-y-5">
      <div className="flex items-center gap-x-2">
        <div className="grow">
          <label className="font-inter-semibold-13px text-gray-800">
            Enable long test runs
          </label>
          <p className="font-inter-normal-12px text-gray-500">
            Consider setting a rate limit to avoid exhausting your quota
          </p>
        </div>
        <Switch
          enabled={effectiveIsEnabled}
          disabled={forceEnabled}
          onChange={(value) => {
            if (forceEnabled) return;
            setEnabled(value);
            updateFlowVersion(value);
          }}
        />
      </div>
      {effectiveIsEnabled && (
        <div className="space-y-5">
          <LimitTypeSelector />
          <Callout icon={faInfoCircle} type="neutral">
            <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
              <span>
                A new decision is initiated once the previous decision is in
              </span>
              <StatusPill status="completed" />
              <StatusPill status="failed" />
              <StatusPill status="pending" />
              <span>or in</span>
              <StatusPill status="review" />
              <span>state.</span>
            </div>
          </Callout>
        </div>
      )}
    </div>
  );
};
